const { getServiceForLocationId } = require('../../service/tools/territory-lookup')
const { parseTextDate } = require('../../shipment/tools/safe-date-parser')
const getPeriodFromProgram = require('../../report/tools/get-period-from-program')
const createReport = require('../../report/tools/create')
const { bulkUpsert } = require('../../report/data-access/pouchdb-adapter')
const { sanitiseBatchNo } = require('../../batch/tools')
const { SHIPSY_ZONE_MOVEMENT_REMARK } = require('../constants')
const { isSellableZone } = require('../utils')

// Aggregates all sellable-affecting items in the payload into signed
// per-(sku,batch) deltas, then writes one partial count per warehouse.
// Items where neither side (or both sides) is sellable are ignored here —
// the transaction row written by the wrapper is the whole record.
function createZoneMovement ({ mainApi, logger, batches }) {
  return async function zoneMovement (params) {
    if (!params) {
      const err = new Error('zoneMovement: payload is required')
      err.status = 400
      throw err
    }

    const moves = Array.isArray(params.move_inventory_data) ? params.move_inventory_data : []
    if (!moves.length) {
      const err = new Error('zoneMovement: move_inventory_data is required')
      err.status = 400
      throw err
    }

    // Validate + group in a single pass. Non-sellable-affecting moves are
    // skipped (the wrapper's stock_transactions row is their whole record).
    const movesByWarehouse = new Map()
    for (const move of moves) {
      if (!move.warehouse) {
        const err = new Error('zoneMovement: warehouse is required')
        err.status = 400
        throw err
      }
      if (!move.sku_code) {
        const err = new Error('zoneMovement: sku_code is required')
        err.status = 400
        throw err
      }
      const quantity = Number(move.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) {
        const err = new Error('zoneMovement: quantity must be a positive number')
        err.status = 400
        throw err
      }

      const originSellable = isSellableZone(move.source_zone)
      const destSellable = isSellableZone(move.destination_zone)
      if (originSellable === destSellable) {
        if (logger && logger.info) {
          logger.info({
            warehouse: move.warehouse,
            source_zone: move.source_zone,
            destination_zone: move.destination_zone,
            sku_code: move.sku_code
          }, 'zoneMovement: skipping partial count for non-sellable-affecting move')
        }
        continue
      }

      let bucket = movesByWarehouse.get(move.warehouse)
      if (!bucket) {
        bucket = []
        movesByWarehouse.set(move.warehouse, bucket)
      }
      bucket.push({ ...move, quantity, delta: originSellable ? -quantity : quantity })
    }

    for (const [warehouseCode, warehouseMoves] of movesByWarehouse.entries()) {
      const location = await mainApi.location.pgAdapter.getOneByCode(warehouseCode)
      const locationId = location.fsid

      const territory = getServiceForLocationId(locationId)
      const serviceId = typeof territory === 'string'
        ? territory
        : (territory && (territory.service || territory.id))

      if (!serviceId) {
        if (logger && logger.warn) {
          logger.warn({
            warehouse: warehouseCode,
            locationId,
            sku_code: warehouseMoves[0].sku_code
          }, 'zoneMovement: skipping partial count due to missing service')
        }
        continue
      }

      const service = await mainApi.service.get(serviceId)

      let ledger
      try {
        ledger = await mainApi.stock.getLedgerBalanceAsReport({
          locationId,
          service,
          excludeProducts: 'configuration',
          calculateFields: true,
          api: mainApi,
          usePGLedger: true
        })
      } catch (err) {
        throw new Error(`zoneMovement: unable to load ledger for ${locationId}: ${err.message || err}`)
      }

      if (!ledger || !ledger.stock) {
        throw new Error('zoneMovement: ledger did not include stock data')
      }

      // Single pass: lazy-clone touched SKUs from ledger, accumulate batch
      // deltas directly onto clonedStock, track SKU totals, and collect batch
      // metadata for bulkUpsertBatchDetails.
      const stockFromLedger = ledger.stock
      const clonedStock = {}
      const totalsBySku = new Map()
      const batchItems = []

      for (const move of warehouseMoves) {
        const productKey = `product:${move.sku_code}`
        const batchNumber = move.batch_no ? sanitiseBatchNo(move.batch_no) : 'UNKNOWN'
        const batchKey = `${productKey}:manufacturer:unknown:batchNo:${batchNumber}`

        if (!clonedStock[productKey]) {
          const fromLedger = stockFromLedger[productKey] || {}
          clonedStock[productKey] = {
            ...fromLedger,
            fields: { ...(fromLedger.fields || {}) },
            batches: { ...(fromLedger.batches || {}) }
          }
        }

        const currentBatch = clonedStock[productKey].batches[batchKey] || {}
        const currentBatchFields = currentBatch.fields || {}
        const currentBatchAmount = Number(
          (currentBatchFields['field:standard-physical-count'] || {}).amount || 0
        )
        clonedStock[productKey].batches[batchKey] = {
          ...currentBatch,
          fields: {
            ...currentBatchFields,
            'field:standard-physical-count': { amount: currentBatchAmount + move.delta }
          }
        }

        totalsBySku.set(move.sku_code, (totalsBySku.get(move.sku_code) || 0) + move.delta)

        batchItems.push({
          skuCode: move.sku_code,
          batchNo: batchNumber,
          manufacturer: 'unknown',
          expiry: parseTextDate(move.expiry_date) || new Date('2099-12-31').toJSON()
        })
      }

      // Finalize: apply SKU-level totals + zone-movement remark in one walk.
      // The Shipsy request_id is appended so partial-count docs remain
      // traceable back to the source callback.
      const remarkAmount = params.request_id != null
        ? `${SHIPSY_ZONE_MOVEMENT_REMARK}:${params.request_id}`
        : SHIPSY_ZONE_MOVEMENT_REMARK
      for (const [skuCode, totalDelta] of totalsBySku.entries()) {
        const productKey = `product:${skuCode}`
        const fields = clonedStock[productKey].fields
        const existingQuantity = Number(
          (fields['field:standard-physical-count'] || {}).amount || 0
        )
        const newQuantity = existingQuantity + totalDelta
        fields['field:standard-physical-count'] = { amount: newQuantity }
        fields['field:shelflife-balance'] = { amount: newQuantity }
        fields['field:standard-remark'] = { amount: remarkAmount }
      }

      await batches.bulkUpsertBatchDetails(batchItems)

      const now = new Date().toISOString()
      const referenceDate = parseTextDate(warehouseMoves[0].created_at) || now
      const period = getPeriodFromProgram(service.program, referenceDate, true)
      const username = warehouseMoves[0].created_by

      const stockCountDoc = createReport({
        locationId,
        service,
        period,
        username,
        createdAt: now,
        submittedAt: now,
        stock: clonedStock,
        partialCount: true
      })
      stockCountDoc.documents = ledger.documents || {}
      stockCountDoc.partialCount = true

      await bulkUpsert(mainApi.state, [stockCountDoc])

      if (logger && logger.info) {
        logger.info({
          warehouse: warehouseCode,
          sku_codes: [...totalsBySku.keys()],
          reportId: stockCountDoc._id
        }, 'zoneMovement: created partial count from Shipsy zone movement')
      }
    }
  }
}

module.exports = createZoneMovement
